import React, { useState, useEffect, useRef, useCallback } from 'react';

const GRID_SIZE = 40;
const STAGE_RADIUS = 80;
const TABLE_SIZE_6 = 30;
const TABLE_SIZE_10 = 42;

function TableLayout({ onTableDoubleClick }) {
  const [tables, setTables] = useState([]);
  const [positions, setPositions] = useState({});
  const [zoom, setZoom] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dragging, setDragging] = useState(null);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState(null);
  const [selectedTable, setSelectedTable] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    Promise.all([
      fetch('/api/admin/seating').then(r => r.json()),
      fetch('/api/admin/layout').then(r => r.json()),
    ]).then(([seatingData, layoutData]) => {
      setTables(seatingData.tables || []);
      setPositions(layoutData.positions || {});
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const saveLayout = useCallback(async (newPositions) => {
    setSaving(true);
    await fetch('/api/admin/layout', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ positions: newPositions }),
    });
    setSaving(false);
  }, []);

  const snapToGrid = (val) => Math.round(val / GRID_SIZE) * GRID_SIZE;

  // Convert screen coordinates to canvas coordinates
  const screenToCanvas = useCallback((clientX, clientY) => {
    const rect = containerRef.current.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    return {
      x: (clientX - rect.left - centerX) / zoom - pan.x,
      y: (clientY - rect.top - centerY) / zoom - pan.y,
    };
  }, [zoom, pan]);

  const handleMouseDown = (e, tableId) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    const pos = positions[tableId] || { x: 0, y: 0 };
    const canvasPos = screenToCanvas(e.clientX, e.clientY);
    setDragging({
      tableId,
      offsetX: canvasPos.x - pos.x,
      offsetY: canvasPos.y - pos.y,
      startX: e.clientX,
      startY: e.clientY,
      moved: false,
    });
  };

  const handleMouseMove = useCallback((e) => {
    if (dragging) {
      const dx = Math.abs(e.clientX - dragging.startX);
      const dy = Math.abs(e.clientY - dragging.startY);
      if (dx > 3 || dy > 3) {
        dragging.moved = true;
      }
      const canvasPos = screenToCanvas(e.clientX, e.clientY);
      const x = snapToGrid(canvasPos.x - dragging.offsetX);
      const y = snapToGrid(canvasPos.y - dragging.offsetY);
      setPositions(prev => ({ ...prev, [dragging.tableId]: { x, y } }));
    } else if (isPanning && panStart) {
      setPan({
        x: panStart.panX + (e.clientX - panStart.mouseX) / zoom,
        y: panStart.panY + (e.clientY - panStart.mouseY) / zoom,
      });
    }
  }, [dragging, isPanning, panStart, zoom, screenToCanvas]);

  const handleMouseUp = useCallback(() => {
    if (dragging) {
      if (!dragging.moved) {
        setSelectedTable(prev => prev === dragging.tableId ? null : dragging.tableId);
      } else {
        saveLayout({ ...positions });
      }
      setDragging(null);
    } else if (isPanning && panStart) {
      const dx = Math.abs(panStart.mouseX - (panStart.mouseX + (pan.x - panStart.panX) * zoom));
      // Close popup when clicking on empty canvas (no movement)
      setSelectedTable(null);
    }
    setIsPanning(false);
    setPanStart(null);
  }, [dragging, positions, saveLayout, isPanning, panStart, pan, zoom]);

  const handleContainerMouseDown = (e) => {
    if (e.button !== 0) return;
    setIsPanning(true);
    setPanStart({ mouseX: e.clientX, mouseY: e.clientY, panX: pan.x, panY: pan.y });
  };

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  const handleWheel = (e) => {
    e.preventDefault();
    setZoom(prev => Math.min(3, Math.max(0.3, prev - e.deltaY * 0.001)));
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, []);

  if (loading) return <div className="admin-container"><p>Loading...</p></div>;

  const getTableRadius = (table) => table.size <= 6 ? TABLE_SIZE_6 : TABLE_SIZE_10;

  return (
    <div className="layout-container">
      <div className="layout-controls">
        <button onClick={() => setZoom(z => Math.min(3, z + 0.2))}>+</button>
        <span>{Math.round(zoom * 100)}%</span>
        <button onClick={() => setZoom(z => Math.max(0.3, z - 0.2))}>-</button>
        <button onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }}>Reset</button>
        <button onClick={() => { setSelectedTable(null); window.print(); }}>Print</button>
        {saving && <span className="saving-indicator">Saving...</span>}
      </div>

      <div
        className="layout-canvas"
        ref={containerRef}
        onMouseDown={handleContainerMouseDown}
      >
        <div
          className="layout-inner"
          style={{
            transform: `translate(-50%, -50%) scale(${zoom}) translate(${pan.x}px, ${pan.y}px)`,
          }}
        >
          {/* Grid dots */}
          <svg className="layout-grid">
            <defs>
              <pattern id="grid" width={GRID_SIZE} height={GRID_SIZE} patternUnits="userSpaceOnUse">
                <circle cx="1" cy="1" r="1" fill="#ccc" />
              </pattern>
            </defs>
            <rect x="-2000" y="-2000" width="4000" height="4000" fill="url(#grid)" />
          </svg>

          {/* Stage */}
          <div
            className="layout-stage"
            style={{
              width: STAGE_RADIUS * 2,
              height: STAGE_RADIUS * 2,
            }}
          >
            Stage
          </div>

          {/* Tables */}
          {tables.map(table => {
            const radius = getTableRadius(table);
            const pos = positions[table.id] || { x: 0, y: 0 };
            const seated = table.seats.filter(Boolean).length;

            return (
              <div
                key={table.id}
                className={`layout-table${dragging?.tableId === table.id ? ' layout-table-dragging' : ''}`}
                style={{
                  width: radius * 2,
                  height: radius * 2,
                  left: pos.x,
                  top: pos.y,
                  cursor: dragging ? 'grabbing' : 'grab',
                }}
                onMouseDown={(e) => handleMouseDown(e, table.id)}
                onDoubleClick={() => onTableDoubleClick && onTableDoubleClick(table.id)}
              >
                <div className="layout-table-name">{table.name}</div>
                <div className="layout-table-count">{seated}/{table.size}</div>

                {selectedTable === table.id && (
                  <div className="layout-popup" onClick={e => e.stopPropagation()}>
                    <div className="layout-popup-title">{table.name} ({seated}/{table.size})</div>
                    <ul className="layout-popup-list">
                      {table.seats.map((guest, i) => (
                        <li key={i} className={guest ? '' : 'layout-popup-empty'}>
                          {guest || 'Empty'}
                        </li>
                      ))}
                      {(table.overflow || []).map((kid, i) => (
                        <li key={`o${i}`} className="layout-popup-overflow">{kid}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default TableLayout;
